import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  PhoneForwarded as PhoneForwardedIcon,
  Dashboard as DashboardIcon,
  History as HistoryIcon,
  Block as BlockIcon,
  Settings as SettingsIcon,
  BarChart as ChartIcon,
  Paid as PaidIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Shield as ShieldIcon,
} from '@mui/icons-material';

const drawerWidth = 240;
const collapsedWidth = 0;
const CONFIG_NAVIGATION_GUARD_KEY = '__scammerDetectionConfirmNavigation';

const canNavigateAway = () => {
  const confirmNavigation = window[CONFIG_NAVIGATION_GUARD_KEY];
  return typeof confirmNavigation !== 'function' || confirmNavigation();
};

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  { text: 'Call History', icon: <HistoryIcon />, path: '/history' },
  { text: 'Blocked Numbers', icon: <BlockIcon />, path: '/blocked' },
  { text: 'Statistics', icon: <ChartIcon />, path: '/stats' },
  { text: 'Risk Meter', icon: <ShieldIcon />, path: '/risk-meter' },
  { text: 'Cost Summary', icon: <PaidIcon />, path: '/cost-summary' },
  { text: 'SIP Simulator', icon: <PhoneForwardedIcon />, path: '/sip-sim' },
  { text: 'Configuration', icon: <SettingsIcon />, path: '/config' },
];

export default function Layout({
  children,
  themeMode = 'dark',
  onToggleThemeMode,
  currentUser,
  onLogout,
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [accountAnchorEl, setAccountAnchorEl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const accountMenuOpen = Boolean(accountAnchorEl);
  const username = currentUser?.username || 'Admin';
  const roles = Array.isArray(currentUser?.roles) ? currentUser.roles.join(', ') : '';
  const accountInitial = username.charAt(0).toUpperCase();

  const handleMobileDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopDrawerToggle = () => {
    setDesktopOpen(!desktopOpen);
  };

  const handleAccountMenuOpen = (event) => {
    setAccountAnchorEl(event.currentTarget);
  };

  const handleAccountMenuClose = () => {
    setAccountAnchorEl(null);
  };

  const handleLogout = () => {
    if (!canNavigateAway()) {
      return;
    }
    handleAccountMenuClose();

    if (onLogout) {
      onLogout();
    }
  };

  const drawer = (
    <div>
      <Toolbar sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL}/logo.png`}
          alt="Scammer Detection"
          sx={{ height: 56, width: 'auto', objectFit: 'contain' }}
        />
        <IconButton
          aria-label="close drawer"
          onClick={() => (isDesktop ? handleDesktopDrawerToggle() : handleMobileDrawerToggle())}
          edge="end"
          size="small"
        >
          <ChevronLeftIcon />
        </IconButton>
      </Toolbar>
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => {
                if (location.pathname === item.path || canNavigateAway()) {
                  navigate(item.path);
                }
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  const currentDrawerWidth = desktopOpen ? drawerWidth : collapsedWidth;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { sm: `${currentDrawerWidth}px` },
          transition: 'all 0.3s ease',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            edge="start"
            onClick={() => (isDesktop ? handleDesktopDrawerToggle() : handleMobileDrawerToggle())}
            sx={{
              mr: 2,
              display: { xs: 'inline-flex', md: desktopOpen ? 'none' : 'inline-flex' },
            }}
            title={isDesktop ? 'Show Menu' : 'Open Menu'}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            {menuItems.find(item => item.path === location.pathname)?.text || 'Dashboard'}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
            <Tooltip title={themeMode === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}>
              <IconButton
                color="inherit"
                aria-label={themeMode === 'dark' ? 'switch to light theme' : 'switch to dark theme'}
                onClick={onToggleThemeMode}
                size="large"
              >
                {themeMode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Account menu">
              <IconButton
                color="inherit"
                aria-label="open account menu"
                aria-controls={accountMenuOpen ? 'account-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={accountMenuOpen ? 'true' : undefined}
                onClick={handleAccountMenuOpen}
                size="large"
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    fontSize: '0.95rem',
                    fontWeight: 700,
                  }}
                >
                  {accountInitial}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              id="account-menu"
              anchorEl={accountAnchorEl}
              open={accountMenuOpen}
              onClose={handleAccountMenuClose}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            >
              <MenuItem disabled>
                <ListItemIcon>
                  <PersonIcon fontSize="small" />
                </ListItemIcon>
                <Box>
                  <Typography variant="body2" color="text.primary">
                    {username}
                  </Typography>
                  {roles && (
                    <Typography variant="caption" color="text.secondary">
                      {roles}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
              {onLogout && (
                <Divider />
              )}
              {onLogout && (
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              )}
            </Menu>
          </Stack>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: currentDrawerWidth }, flexShrink: { sm: 0 }, transition: 'all 0.3s ease' }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleMobileDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="persistent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: currentDrawerWidth,
              transition: 'all 0.3s ease',
              overflow: 'hidden',
            },
          }}
          open={desktopOpen}
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          transition: 'all 0.3s ease',
          bgcolor: 'background.default',
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
}
